/**
 * DateCell Component
 *
 * Shared cell component for rendering date fields consistently
 * across all entity tables and QueryBuilder.
 */

import React from 'react';

import { formatDate } from '@/utils/date.util';

export interface DateCellProps {
  entity?: {
    [key: string]: any;
  };
  field?: string;
  value?: string;
}

/**
 * Renders a formatted date value
 *
 * Can receive date value either:
 * - Directly via `value` prop (preferred)
 * - Via entity + field combination
 *
 * @example
 * ```typescript
 * <DateCell value={asset.created} />
 * <DateCell entity={asset} field="created" />
 * ```
 */
export const DateCell: React.FC<DateCellProps> = React.memo(
  ({ entity, field, value }) => {
    const dateValue = value || (field && entity?.[field]);

    if (!dateValue) {
      return <span className="text-chariot-text-secondary">-</span>;
    }

    const formatted = formatDate(dateValue);

    return (
      <span className="text-chariot-text-primary" title={dateValue}>
        {formatted}
      </span>
    );
  }
);

DateCell.displayName = 'DateCell';
